import React, { useMemo } from 'react';
import { Shield, Plus, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

export default function RoleSkillTab({ roles, skills, onAddRole, onEditRole, onDeleteRole }) {
  // Map skills to roles based on skill IDs or categories
  const rolesWithSkills = useMemo(() => {
    return roles.map(role => {
      // Get technical skills - either from role.technical_skills or filter by category
      let technical_skills = role.technical_skills || [];
      if (!technical_skills.length && role.skill_ids) {
        technical_skills = (role.skill_ids || [])
          .map(skillId => skills.find(s => s.id === skillId))
          .filter(s => s && s.category === 'technical');
      }

      // Get soft skills - either from role.soft_skills or filter by category
      let soft_skills = role.soft_skills || [];
      if (!soft_skills.length && role.skill_ids) {
        soft_skills = (role.skill_ids || [])
          .map(skillId => skills.find(s => s.id === skillId))
          .filter(s => s && s.category === 'soft_skill');
      }

      return {
        ...role,
        technical_skills,
        soft_skills,
      };
    });
  }, [roles, skills]);

  return (
    <div className="space-y-4">
      {/* ROLE & KOMPETENSI */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 !text-lg">
              <Shield className="w-5 h-5" />
              Role & Kompetensi
            </CardTitle>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={onAddRole}>
              <Plus className="w-4 h-4 mr-2" /> Tambah Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Nama Role</TableHead>
                <TableHead>Technical Skills</TableHead>
                <TableHead>Soft Skills</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rolesWithSkills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Tidak ada data
                  </TableCell>
                </TableRow>
              ) : (
                rolesWithSkills.map(item => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {(item.technical_skills || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(item.technical_skills || []).slice(0, 3).map((skill, i) => {
                            const skillName = typeof skill === 'string' ? skill : skill.name;
                            const proficiency = typeof skill === 'string' ? '' : skill.proficiency;
                            return (
                              <Badge key={i} variant="outline" className="text-xs">
                                {skillName} {proficiency && `(${proficiency})`}
                              </Badge>
                            );
                          })}
                          {(item.technical_skills || []).length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{item.technical_skills.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {(item.soft_skills || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(item.soft_skills || []).slice(0, 3).map((skill, i) => {
                            const skillName = typeof skill === 'string' ? skill : skill.name;
                            const proficiency = typeof skill === 'string' ? '' : skill.proficiency;
                            return (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {skillName} {proficiency && `(${proficiency})`}
                              </Badge>
                            );
                          })}
                          {(item.soft_skills || []).length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{item.soft_skills.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                          item.status === 'active'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-400 text-white'
                        }`}
                      >
                        {item.status === 'active' ? 'Aktif' : 'Tidak Aktif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => onEditRole(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onDeleteRole(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
